import {
  autoUpdate,
  FloatingPortal,
  flip,
  offset,
  size as sizeMiddleware,
  useClick,
  useDismiss,
  useFloating,
  useInteractions,
  useRole,
} from '@floating-ui/react'
import { CaretDownIcon } from '@phosphor-icons/react'
import React, { useEffect, useRef, useState } from 'react'

import { COUNTRIES, CountryData, DEFAULT_US_PHONE_NUMBER_PLACEHOLDER } from '@/constants'

import { Icon } from '../icon'
import { TextInput } from '../text-input'
import * as S from './phone-input.styled'
import { PhoneInputProps } from './phone-input.types'
import {
  buildFullPhoneNumber,
  formatToNational,
  getCountryByCode,
  parsePhoneValue,
} from './phone-input.utils'

export const PhoneInput = ({
  value,
  size = 'medium',
  initialValue,
  onChange,
  defaultCountry = 'US',
  disabled,
  placeholder: placeholderProp,
  ...props
}: PhoneInputProps) => {
  const [isOpen, setIsOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')

  // Seed state from `value` first (controlled), then `initialValue` (uncontrolled).
  const seedValue = value ?? initialValue

  const seedParsed = seedValue ? parsePhoneValue(seedValue) : null

  const [selectedCountry, setSelectedCountry] = useState<CountryData>(
    () => seedParsed?.country ?? getCountryByCode(defaultCountry) ?? COUNTRIES[0],
  )

  const [inputValue, setInputValue] = useState<string>(() => seedParsed?.nationalNumber ?? '')

  // Tracks the last E.164 string we emitted so the sync effect below can distinguish
  // "parent echoing our own onChange back" (skip) from "external value change" (sync).
  const lastEmittedRef = useRef<string | undefined>(value)

  const inputRef = useRef<HTMLInputElement>(null)

  const filteredCountries = !searchQuery
    ? COUNTRIES
    : COUNTRIES.filter((country) => {
        const query = searchQuery.toLowerCase()
        return (
          country.name.toLowerCase().includes(query) ||
          country.dialCode.includes(query) ||
          country.code.toLowerCase().includes(query)
        )
      })

  const { refs, floatingStyles, context } = useFloating({
    middleware: [
      offset(4),
      flip(),
      // Note: match dropdown width to the full field box, not just the selector button.
      sizeMiddleware({
        apply({ rects, elements }) {
          Object.assign(elements.floating.style, {
            width: `${rects.reference.width}px`,
          })
        },
      }),
    ],
    onOpenChange(open) {
      setIsOpen(open)
      // Note: clear search when the dropdown closes (dismiss, country select, etc.)
      if (!open) setSearchQuery('')
    },
    open: isOpen,
    placement: 'bottom-start',
    whileElementsMounted: autoUpdate,
  })

  const click = useClick(context)
  const dismiss = useDismiss(context)
  const role = useRole(context, { role: 'listbox' })

  const { getReferenceProps, getFloatingProps } = useInteractions([click, dismiss, role])

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = e.target.value
    setInputValue(formatToNational(rawValue, selectedCountry.code))
    const e164 = buildFullPhoneNumber(selectedCountry.code, rawValue)
    lastEmittedRef.current = e164
    onChange(e164)
  }

  const handleCountrySelect = (country: CountryData) => {
    setSelectedCountry(country)
    setIsOpen(false)
    // Note: move focus to the phone input so the user can keep typing straight after
    // picking a country, without an extra click back into the field.
    inputRef.current?.focus()

    if (!inputValue) return

    setInputValue(formatToNational(inputValue, country.code))
    const e164 = buildFullPhoneNumber(country.code, inputValue)
    lastEmittedRef.current = e164
    onChange(e164)
  }

  const placeholder =
    placeholderProp ??
    (selectedCountry.code === 'US' ? DEFAULT_US_PHONE_NUMBER_PLACEHOLDER : 'Enter phone number')

  useEffect(() => {
    if (value === undefined || value === lastEmittedRef.current) return

    const parsed = value ? parsePhoneValue(value) : null
    if (parsed) {
      setSelectedCountry(parsed.country)
      setInputValue(parsed.nationalNumber)
    } else {
      // Note: value was cleared (or is unparseable) — blank the national number but
      // keep the currently selected country.
      setInputValue('')
    }
    lastEmittedRef.current = value
  }, [value])

  return (
    <>
      <TextInput
        ref={inputRef}
        type="tel"
        {...props}
        disabled={disabled}
        inputWrapperRef={refs.setReference}
        leadingSlot={
          // Note: getReferenceProps() goes on the button for click/keyboard/ARIA;
          // refs.setReference is on TextInput's InputWrapper via inputWrapperRef so the
          // dropdown measures the full field width, not just the selector button.
          <S.CountrySelector disabled={disabled} type="button" {...getReferenceProps()}>
            <S.DialCode>{selectedCountry.dialCode}</S.DialCode>
            <S.CaretIcon $isOpen={isOpen}>
              <Icon icon={CaretDownIcon} size="1.2rem" />
            </S.CaretIcon>
          </S.CountrySelector>
        }
        placeholder={placeholder}
        size={size}
        value={inputValue}
        onChange={handleInputChange}
      />

      {isOpen && (
        <FloatingPortal>
          <S.Dropdown ref={refs.setFloating} style={floatingStyles} {...getFloatingProps()}>
            <S.SearchInput
              placeholder="Search countries..."
              value={searchQuery}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchQuery(e.target.value)}
            />
            {filteredCountries.map((country) => (
              <S.Option
                key={country.code}
                $isSelected={country.code === selectedCountry.code}
                type="button"
                onClick={() => handleCountrySelect(country)}
              >
                <S.OptionName>{country.name}</S.OptionName>
                <S.OptionDialCode>{country.dialCode}</S.OptionDialCode>
              </S.Option>
            ))}
          </S.Dropdown>
        </FloatingPortal>
      )}
    </>
  )
}
